// tr landing pages: see config/localeHubs.ts for the rules and
// config/hubs/tr.json for the slug of each page type (keep them in step).
//
// Volumes are Turkey, monthly (OpenSEO, Oct 2026); long tail from Google
// autocomplete (hl=tr, gl=tr). Turkish attaches suffixes to names, so the copy
// avoids putting one on a voice or brand name where it can be rephrased.
// Skipped on purpose: "yapay zeka ses" (1.3k) reads as text-to-speech intent.
import type { HubCategory, HubConfig } from "../hubPages";

const CATEGORIES_TR: HubCategory[] = [
  { label: "Müzik", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Çizgi film", filter: "cartoon" },
  { label: "Oyun", filter: "gaming" },
  { label: "K-pop", filter: "korean" },
  { label: "Ünlüler", filter: "public figure" },
];

// Nearly every Turkish query has an "ücretsiz" variant; answer it honestly.
const FREE_TR = {
  q: "Jammable ücretsiz mi?",
  a: "Tüm sesleri keşfetmek ve örnek cover'ları dinlemek ücretsiz. Cover yapmak, sesini değiştirmek, ses klonlamak veya metinden sese kullanmak için bir plan gerekir. Güncel seçenekler için fiyatlar sayfasına göz at.",
};

export const HUBS: Record<string, HubConfig> = {
  // "ses değiştirici" 12.1k (KD 0) and "ses değiştirme" 12.1k (likely the same
  // searches grouped), "yapay zeka ses değiştirici" 390. Autocomplete: "ses
  // değiştirici program / uygulama / pc / site / online / ücretsiz", "kadın
  // sesi", "ses değiştirme ai", "yapay zeka anlık / gerçek zamanlı ses
  // değiştirici". Results are store apps plus online AI changers (Kapwing,
  // Canva, voicechanger.live, EaseUS). Real time is answered in the FAQ.
  "ses-degistirici": {
    slug: "ses-degistirici",
    group: "voice-changer",
    path: "/tr/ses-degistirici",
    locale: "tr",
    h1: "Yapay zeka ses değiştirici",
    highlight: "ses değiştirici",
    title: "Ses Değiştirici: Yapay Zeka ile Sesini Değiştir | Jammable",
    metaDescription:
      "Online yapay zeka ses değiştirici: sesini bir karakterin, şarkıcının veya ünlünün sesine çevir. 100.000+ gerçekçi ses. Program indirmeden kaydet veya yükle.",
    heroSub:
      "Bir kayıt yap veya yükle, kime benzemek istediğini seç ve dönüşen sesini saniyeler içinde dinle.",
    intro:
      "Yapay zeka ses değiştirici sesini sadece bozmaz, onu gerçek bir başka sese dönüştürür. Karakterler, şarkıcılar, ünlüler ya da kendi eğittiğin bir ses: ses değiştirme artık birkaç tıklama.",
    appName: "Jammable Yapay Zeka Ses Değiştirici",
    steps: [
      {
        title: "Kaydet veya yükle",
        body: "Mikrofonla kendini kaydet ya da dönüştürmek istediğin ses dosyasını yükle.",
      },
      {
        title: "Bir ses seç",
        body: "Karakterlerden ve çizgi filmlerden şarkıcılara ve ünlülere 100.000+ yapay zeka sesi.",
      },
      {
        title: "Sesini değiştir",
        body: "Saniyeler içinde yeni sesle hazırlanmış kaydın indirmeye hazır.",
      },
    ],
    faqs: [
      {
        q: "Yapay zeka ses değiştirici nedir?",
        a: "Kaydedilmiş bir sesi alıp yapay zeka ile başka bir sese dönüştüren bir araçtır. Efektlerle çalışan klasik bir ses değiştirme programının aksine sonuç, seçtiğin gerçek ses gibi duyulur.",
      },
      {
        q: "Yapay zeka ile sesimi nasıl değiştiririm?",
        a: "Bir kayıt yap veya yükle, istediğin sesi seç ve oluştur'a bas. Jammable kaydını dönüştürür ve sana indirebileceğin temiz bir sonuç verir.",
      },
      {
        q: "Gerçek zamanlı veya telefon görüşmelerinde çalışır mı?",
        a: "Hayır. Jammable kaydedilmiş veya yüklenmiş sesi dönüştürür; canlı mikrofonu veya telefon görüşmelerini değil. Bu yüzden sonuç, anlık bir ses değiştiriciye göre çok daha gerçekçi olur. Dönüşen sesi indirip istediğin yerde kullanabilirsin.",
      },
      {
        q: "Program veya uygulama indirmem gerekiyor mu?",
        a: "Hayır. Jammable tarayıcıda çalışan online bir ses değiştirici; bilgisayarda da telefonda da kurulum gerekmez.",
      },
      {
        q: "Sesimi bir karakterin veya kadın sesine çevirebilir miyim?",
        a: "Evet. Kütüphanede binlerce karakter, çizgi film ve oyun sesinin yanında kadın ve erkek şarkıcı sesleri var; her gün yenileri ekleniyor.",
      },
      FREE_TR,
    ],
    categories: CATEGORIES_TR,
    related: [
      { label: "AI cover", href: "/tr/ai-cover" },
      { label: "Ses klonlama", href: "/tr/ses-klonlama" },
      { label: "Tüm sesler", href: "/models" },
    ],
  },

  // "ai cover" 1.9k (KD 17), "yapay zeka şarkı söyletme" 720 (KD 0).
  // Autocomplete: "ai cover nasıl yapılır", "ai cover yapma", "ai cover
  // türkçe", "kendi sesimle ai cover", "yapay zeka cover yapma", "şarkıyı
  // başkasının sesiyle söyletme", "yapay zeka şarkı söyletme online /
  // uygulaması / sitesi / ücretsiz", and named Turkish singers (not named
  // here: we don't know which of their voices exist).
  "ai-cover": {
    slug: "ai-cover",
    group: "cover",
    path: "/tr/ai-cover",
    locale: "tr",
    h1: "AI cover: yapay zeka ile şarkı söyletme",
    highlight: "AI cover",
    title: "AI Cover Yap: Yapay Zeka ile Şarkı Söyletme | Jammable",
    metaDescription:
      "100.000+ sesle AI cover yap: şarkıcılar, karakterler ve ünlüler. Yapay zeka ile bir kaydı başka bir sesle söylet, saniyeler içinde stüdyo kalitesinde.",
    heroSub:
      "Bir ses seç, kullanma hakkına sahip olduğun bir kaydı ekle; Jammable saniyeler içinde AI cover'ını hazırlasın.",
    intro:
      "Bir şarkıyı başka bir sesle söyletmek eskiden stüdyo ve saatlerce miks demekti. Jammable'da sesi seçer, kaydını yüklersin, gerisini yapay zeka halleder: vokali ayırır, dönüştürür ve yeniden miksler.",
    appName: "Jammable AI Cover",
    steps: [
      {
        title: "Bir ses seç",
        body: "100.000+ yapay zeka sesi: şarkıcılar, rapçiler, çizgi film ve anime karakterleri ya da kendi eğittiğin bir ses.",
      },
      {
        title: "Kaydını ekle",
        body: "Bir dosya yükle, kendini şarkı söylerken kaydet veya yapay zeka ile özgün bir şarkı oluştur.",
      },
      {
        title: "Cover'ını oluştur",
        body: "Jammable sesi dönüştürür ve sonucu miksler. İndir, TikTok'ta veya YouTube'da paylaş.",
      },
    ],
    faqs: [
      {
        q: "AI cover nedir?",
        a: "Bir şarkının yapay zeka yardımıyla başka bir sesle söylenmiş halidir. Bir kaydı ya da kendi sesini alıp bir sanatçının, karakterin veya eğittiğin bir sesin sesine dönüştürürsün; melodi ve sözler aynı kalır.",
      },
      {
        q: "AI cover nasıl yapılır?",
        a: "Jammable'da sesi seç, kaydı yükle ve oluştur'a bas. Jammable vokali müzikten ayırır, seçtiğin sese dönüştürür ve her şeyi bir dakikadan kısa sürede yeniden miksler. Yalnızca kullanma hakkına sahip olduğun kayıtları kullan.",
      },
      {
        q: "Türkçe şarkılarla çalışır mı?",
        a: "Evet. Yapay zeka sözleri değil sesi dönüştürür, bu yüzden Türkçe kayıtlar da aynı şekilde çalışır.",
      },
      {
        q: "Kendi sesimle AI cover yapabilir miyim?",
        a: "Evet. En az 6 dakikalık temiz bir kayıtla kendi sesinin modelini eğit ve istediğin cover'da kullan.",
      },
      FREE_TR,
      {
        q: "AI cover'lar ne kadar iyi duyulur?",
        a: "Gerçek bir kayda çok yakın duyulabilir. Jammable yüksek kaliteli ses dönüştürme ve otomatik miks kullanır; ücretli planlarda daha yüksek kalite ayarları var.",
      },
    ],
    categories: CATEGORIES_TR,
    related: [
      { label: "Yapay zeka ses değiştirici", href: "/tr/ses-degistirici" },
      { label: "Ses klonlama", href: "/tr/ses-klonlama" },
      { label: "Ses eğit", href: "/train" },
    ],
  },

  // "ses klonlama" 1.6k (KD 0). Autocomplete: "ses klonlama ai / yapay zeka",
  // "ses klonlama nedir", "ses klonlama nasıl yapılır", "ses klonlama
  // ücretsiz", "ses klonlama şarkı", "kendi sesimi klonlama"; ElevenLabs and
  // MiniMax also show (speech cloning).
  "ses-klonlama": {
    slug: "ses-klonlama",
    group: "clone",
    path: "/tr/ses-klonlama",
    locale: "tr",
    h1: "Yapay zeka ile ses klonlama",
    highlight: "ses klonlama",
    title: "Ses Klonlama: Yapay Zeka ile Sesinin Modelini Oluştur | Jammable",
    metaDescription:
      "6 dakikalık kayıttan yapay zeka ile ses klonlama. Klonlanmış sesini cover'larda, kayıtlarda ve metinden seste kullan. Kendi RVC modellerini de yükle.",
    heroSub:
      "En az 6 dakikalık temiz bir kayıt yükle; Jammable o sesin modelini eğitsin, sen de cover'larda ve metinden seste kullan.",
    intro:
      "Yapay zeka ile ses klonlama, bir sesin nasıl duyulduğunu öğrenen bir model eğitmek demektir. Sonra o sese istediğini söyletebilir veya şarkı söyletebilirsin. Yalnızca kendi sesini veya kullanma izni olan sesleri klonla.",
    appName: "Jammable Ses Klonlama",
    steps: [
      {
        title: "Kaydını yükle",
        body: "En az 6 dakika şarkı veya konuşma sesi; temiz ve uzun sessizlikler olmadan.",
      },
      {
        title: "Modeli eğit",
        body: "Jammable sesi senin için eğitir. Ekran kartına veya teknik bilgiye gerek yok.",
      },
      {
        title: "Sesini kullan",
        body: "Cover yap, kayıtları dönüştür veya bir metin yaz ve klonlanmış sesinle dinle.",
      },
    ],
    faqs: [
      {
        q: "Ses klonlama nedir?",
        a: "Bir sesin örneklerinden, o sesi taklit edebilen bir yapay zeka modeli eğitmektir. Jammable'da klonlanmış bir ses şarkı söyleyebilir, kayıtları dönüştürebilir ve yazdığın metni okuyabilir.",
      },
      {
        q: "Ses klonlama nasıl yapılır?",
        a: "Sesin en az 6 dakikalık temiz kaydını yükle, bir isim ver ve Jammable modeli eğitsin. Bittiğinde ses, kullanıma hazır şekilde kütüphanende görünür.",
      },
      {
        q: "Bir sesi klonlamak için ne kadar kayıt gerekir?",
        a: "En az 6 dakika temiz ses; arka planda müzik veya uzun sessizlikler olmadan. Daha fazla ve daha kaliteli kayıt, daha gerçekçi bir sonuç verir.",
      },
      {
        q: "Elimdeki bir RVC modelini yükleyebilir miyim?",
        a: "Evet. .pth ve .index dosyalarını kütüphanenden yükle, ses anında kullanıma hazır olur.",
      },
      {
        q: "Başka birinin sesini klonlayabilir miyim?",
        a: "Sadece izni varsa. Kendi sesini veya kullanma hakkına sahip olduğun sesleri klonla.",
      },
      FREE_TR,
    ],
    categories: CATEGORIES_TR,
    related: [
      { label: "Sesimi eğit", href: "/train" },
      { label: "AI cover", href: "/tr/ai-cover" },
      { label: "Yapay zeka ses değiştirici", href: "/tr/ses-degistirici" },
    ],
  },
};
